import { useId } from 'react';
import { strings } from '@/shared/strings';
import { Button } from '@/ui/primitives/Button';
import styles from './Pagination.module.css';

export interface PaginationProps {
  /** The page being shown, from 1. */
  page: number;
  pageSize: number;
  /** Every row the filter matches, not the number on this page. */
  total: number;
  /** The sizes this table offers, in the order they are offered. */
  sizes: readonly number[];
  onPage: (page: number) => void;
  onPageSize: (size: number) => void;
  /** Names the control for a screen reader, since a screen may carry two tables. */
  label: string;
}

/**
 * How many rows there are, which page is on screen, and how big a page is.
 * Section 10.4 of the request of 16 September 2026.
 *
 *   Showing 26 to 50 of 214        [25 v] per page     < Previous   3 of 9   Next >
 *
 * The count sentence is on the left and is the part that matters: it is what
 * tells somebody there are two hundred cases rather than twenty-five. Every
 * list endpoint has always been paged and every screen asked for the cap and
 * rendered the lot, so a long list simply ended, silently, wherever the cap
 * fell.
 *
 * Nothing at all when the rows fit on one page, so a bench with four cases sees
 * no furniture.
 *
 * Previous and Next rather than a row of page numbers: at nine pages the
 * numbers are not worth the width, and the two buttons are a decision pair, so
 * they move together the way section 9 asks.
 */
export function Pagination({
  page,
  pageSize,
  total,
  sizes,
  onPage,
  onPageSize,
  label,
}: PaginationProps) {
  const id = useId();
  const lastPage = Math.max(1, Math.ceil(total / pageSize));

  // One page of rows is a list, not a paged list.
  if (total <= pageSize) return null;

  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);

  return (
    <nav className={styles.pagination} aria-label={label}>
      <p className={styles.showing}>
        {strings.table.showing} <b>{first}</b> {strings.table.to} <b>{last}</b>{' '}
        {strings.table.ofTotal} <b>{total}</b>
      </p>

      <div className={styles.size}>
        <label className={styles.l} htmlFor={id}>
          {strings.table.perPage}
        </label>
        <select
          id={id}
          className={styles.select}
          value={pageSize}
          onChange={(event) => onPageSize(Number(event.target.value))}
        >
          {sizes.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.pager}>
        <Button
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          // Names the page it goes to, not the direction: "Previous" alone
          // tells a screen reader nothing about where it lands.
          aria-label={`${strings.table.previous}, ${strings.table.page} ${page - 1}`}
        >
          {strings.table.previous}
        </Button>
        <span className={styles.pageOf} aria-live="polite">
          {page} {strings.table.of} {lastPage}
        </span>
        <Button
          disabled={page >= lastPage}
          onClick={() => onPage(page + 1)}
          aria-label={`${strings.table.next}, ${strings.table.page} ${page + 1}`}
        >
          {strings.table.next}
        </Button>
      </div>
    </nav>
  );
}
